import React, { useState, useEffect } from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import { getDatabase, ref, onValue } from "firebase/database";
import SEO from "./SEO";

const steps = [
  { title: "Submit the Required Documents", icon: "📄" },
  { title: "Pay the Fee Online", icon: "💳" },
  { title: "Document Verification", icon: "✅" },
  { title: "Receive Your Visa", icon: "✈️" },
];

const strengths = [
  { icon: "🔄", title: "Streamlined Visa Procedures", description: "Efficient processes for quick visa approvals" },
  { icon: "🤝", title: "Personalized Services", description: "Tailored solutions for your specific visa needs" },
  { icon: "👥", title: "Team of Visa Experts", description: "Experienced professionals handling your application" },
  { icon: "🚗", title: "Document Pickup & Delivery", description: "Convenient service for busy travelers" },
  { icon: "📰", title: "Latest Visa Updates", description: "Real-time information on visa policy changes" },
  { icon: "😊", title: "Client Satisfaction", description: "98% success rate with 5-star reviews" },
];

const testimonials = [
  {
    quote: "Our trip with Pearl International was just fantastic. We learnt about Vietnam history and culture. We have also tried delicious food of Vietnam and indulge in different adventures…",
    author: "Dr. Ramesh Keval Chand",
    rating: 5,
  },
  {
    quote: "The visa process was seamless and stress-free. The team guided us at every step and we got our visas well before our travel date.",
    author: "Priya Sharma",
    rating: 5,
  },
  {
    quote: "The visa process was seamless and stress-free. The team guided us at every step and we got our visas well before our travel date.",
    author: "Ishu Patil",
    rating: 5,
  },
];

// Country code mapping for flags
const countryCodes = {
  africa: "af",
  dubai: "ae",
  thailand: "th",
  baku: "az",
  tbilisi: "ge",
  yerevan: "am",
  moscow: "ru",
  almaty: "kz",
  bishkek: "kg",
  helsinki: "fi",
  paris: "fr",
  venice: "it",
  capetown: "za",
  india: "in",
  vietnam: "vn",
  japan: "jp",
  usa: "us",
  "south-africa": "za",
  singapore: "sg",
  malaysia: "my",
  indonesia: "id",
  philippines: "ph",
  australia: "au",
  "new-zealand": "nz",
  canada: "ca",
  uk: "gb",
  germany: "de",
  spain: "es",
  portugal: "pt",
  turkey: "tr",
  egypt: "eg",
};

// Reusable Card Component
const Card = ({ children, style }) => (
  <div style={{
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    padding: "24px",
    transition: "transform 0.3s, box-shadow 0.3s",
    ...style
  }}>
    {children}
  </div>
);

// Step Component
const Step = ({ icon, title, description }) => (
  <div style={{ textAlign: "center", padding: "16px" }}>
    <div style={{
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      backgroundColor: "#dbeafe",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 24px",
      fontSize: "1.75rem",
    }}>
      {icon}
    </div>
    <h3 style={{
      fontSize: "1.125rem",
      fontWeight: "600",
      color: "#1e293b",
      marginBottom: "12px",
    }}>
      {title}
    </h3>
    <p style={{
      color: "#64748b",
      lineHeight: "1.6",
    }}>
      {description || "Our streamlined process ensures quick approval with minimal documentation required."}
    </p>
  </div>
);

// Visa Detail Modal Component
const VisaDetailModal = ({ 
  showModal, 
  closeModal, 
  selectedCountry, 
  visaDetails, 
  modalLoading 
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    travelDate: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          phone: "",
          travelDate: "",
          message: ""
        });
        setSubmitSuccess(false);
      }, 3000);
    }, 1500);
  };

  if (!showModal) return null;

  // Format key to human readable
  const formatKey = (key) => {
    const result = key.replace(/([A-Z])/g, ' $1');
    const withSpaces = result.replace(/[_-]/g, ' ');
    return withSpaces.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      return (
        <ul style={{ paddingLeft: '20px', margin: 0 }}>
          {Object.entries(value).map(([subKey, subValue]) => (
            <li key={subKey}>
              <strong>{formatKey(subKey)}:</strong> {renderValue(subValue)}
            </li>
          ))}
        </ul>
      );
    } else if (Array.isArray(value)) {
      return (
        <ul style={{ paddingLeft: '20px', margin: 0 }}>
          {value.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
    }
    return value || "N/A";
  };

  const renderVisaDetails = () => {
    if (modalLoading) {
      return (
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          height: "200px",
          flexDirection: "column" 
        }}>
          <div style={{
            border: "4px solid rgba(0, 0, 0, 0.1)",
            borderLeftColor: "#2563eb",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            animation: "spin 1s linear infinite",
            marginBottom: "16px"
          }}></div>
          <p>Loading visa details...</p>
        </div>
      );
    }

    if (!visaDetails || Object.keys(visaDetails).length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p>No visa details available for {selectedCountry}</p>
        </div>
      );
    }

    return (
      <div style={{
        padding: "24px",
        borderRadius: "16px",
        background: "linear-gradient(145deg, #f8fafc, #f1f5f9)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "32px",
          paddingBottom: "16px",
          borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
          position: "relative"
        }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #3b82f6, #6366f1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "16px",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 style={{ 
            fontWeight: "700", 
            color: "#1e293b",
            fontSize: "1.5rem",
            margin: "0",
            background: "linear-gradient(90deg, #3b82f6, #10b981)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>
            Complete Visa Details for {selectedCountry.replace(/-/g, " ")}
          </h3>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "24px"
        }}>
          {Object.entries(visaDetails).map(([key, value]) => (
            <div key={key} style={{
              transition: "all 0.3s ease",
              ":hover": {
                transform: "translateY(-5px)"
              }
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px"
              }}>
                <div style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: "#3b82f6",
                  marginRight: "12px",
                  boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.2)"
                }}></div>
                <h4 style={{ 
                  fontWeight: "700", 
                  margin: "0",
                  color: "#3b82f6",
                  fontSize: "1.1rem",
                  position: "relative",
                  display: "inline-block",
                  ":after": {
                    content: '""',
                    position: "absolute",
                    bottom: "-4px",
                    left: "0",
                    width: "0",
                    height: "2px",
                    background: "#3b82f6",
                    transition: "width 0.3s ease"
                  }
                }}>
                  {formatKey(key)}
                </h4>
              </div>
              <div style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                border: "1px solid #e2e8f0",
                transition: "all 0.3s ease",
                ":hover": {
                  boxShadow: "0 8px 24px rgba(59, 130, 246, 0.15)",
                  borderColor: "#bfdbfe"
                }
              }}>
                {key === "Documents Required" ? (
                  <ul style={{
                    margin: 0,
                    paddingLeft: "20px",
                    listStyleType: "none"
                  }}>
                    {value.map((doc, index) => {
                      const cleanDoc = doc.replace(/^\d+:\s*/, "");
                      return (
                        <ul key={index} style={{
                          position: "relative",
                          paddingLeft: "24px",
                          marginBottom: "12px",
                          ":before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: "10px",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: "#3b82f6",
                            transition: "transform 0.2s ease"
                          },
                          ":hover:before": {
                            transform: "scale(1.3)"
                          }
                        }}>
                          {cleanDoc}
                        </ul>
                      );
                    })}
                  </ul>
                ) : (
                  renderValue(value)
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderEnquiryForm = () => (
    <div style={{ padding: "24px" }}>
      <div style={{ 
        background: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
        borderRadius: "12px",
        padding: "32px",
        marginBottom: "32px",
        color: "white",
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{
          position: "absolute",
          top: "-50px",
          right: "-50px",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)"
        }}></div>
        <div style={{
          position: "absolute",
          bottom: "-80px",
          left: "-30px",
          width: "160px",
          height: "160px",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)"
        }}></div>
        
        <h3 style={{ 
          fontSize: "1.5rem", 
          fontWeight: "bold", 
          marginBottom: "16px",
          position: "relative",
          zIndex: 1
        }}>
          Visa Assistance for {selectedCountry.replace(/-/g, " ")}
        </h3>
        <p style={{ 
          fontSize: "1.1rem", 
          marginBottom: "24px",
          position: "relative",
          zIndex: 1
        }}>
          Our visa experts will contact you within 24 hours to assist with your application
        </p>
      </div>
      
      {submitSuccess ? (
        <div style={{ 
          background: "linear-gradient(135deg, #10b981, #34d399)",
          borderRadius: "12px",
          padding: "32px",
          textAlign: "center",
          color: "white",
          marginBottom: "32px",
          animation: "fadeIn 0.5s ease-in"
        }}>
          <div style={{ fontSize: "3rem", marginBottom: "16px" }}>✓</div>
          <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "16px" }}>
            Thank You For Your Enquiry!
          </h3>
          <p style={{ fontSize: "1.1rem" }}>
            Our visa specialist will contact you shortly to assist with your {selectedCountry.replace(/-/g, " ")} visa application.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ animation: "fadeIn 0.5s ease-in" }}>
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "1fr 1fr", 
            gap: "24px",
            marginBottom: "24px"
          }}>
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                fontWeight: "500",
                color: "#334155"
              }}>
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  transition: "all 0.3s",
                  ":focus": {
                    outline: "none",
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.2)"
                  }
                }}
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                fontWeight: "500",
                color: "#334155"
              }}>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  transition: "all 0.3s",
                  ":focus": {
                    outline: "none",
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.2)"
                  }
                }}
                placeholder="john@example.com"
              />
            </div>
            
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                fontWeight: "500",
                color: "#334155"
              }}>
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  transition: "all 0.3s",
                  ":focus": {
                    outline: "none",
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.2)"
                  }
                }}
                placeholder="+91 9876543210"
              />
            </div>
            
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px", 
                fontWeight: "500",
                color: "#334155"
              }}>
                Travel Date
              </label>
              <input
                type="date"
                name="travelDate"
                value={formData.travelDate}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  transition: "all 0.3s",
                  ":focus": {
                    outline: "none",
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.2)"
                  }
                }}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: "24px" }}>
            <label style={{ 
              display: "block", 
              marginBottom: "8px", 
              fontWeight: "500",
              color: "#334155"
            }}>
              Additional Information
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                transition: "all 0.3s",
                ":focus": {
                  outline: "none",
                  borderColor: "#3b82f6",
                  boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.2)"
                }
              }}
              placeholder="Tell us about your travel plans..."
            ></textarea>
          </div>
          
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: "14px 32px",
                background: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
                color: "white",
                fontWeight: "600",
                fontSize: "1rem",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                transition: "all 0.3s",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                position: "relative",
                overflow: "hidden",
                ":hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                },
                ":disabled": {
                  opacity: 0.7,
                  cursor: "not-allowed"
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <span style={{ position: "relative" }}>Processing...</span>
                </>
              ) : (
                "Submit Enquiry"
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "20px",
      animation: "fadeIn 0.3s ease-out"
    }}>
      <div style={{
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        maxWidth: "900px",
        width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        animation: "scaleIn 0.3s ease-out",
        position: "relative"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 30px",
          background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
          color: "white",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}>
          <h2 style={{ 
            fontSize: "1.5rem", 
            fontWeight: "bold", 
            textTransform: "capitalize",
            margin: 0
          }}>
            {selectedCountry.replace(/-/g, " ")} Visa Assistance
          </h2>
          <button onClick={closeModal} style={{
            background: "rgba(255, 255, 255, 0.2)",
            border: "none",
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s",
            ":hover": { 
              background: "rgba(255, 255, 255, 0.3)",
              transform: "rotate(90deg)"
            }
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div style={{
          display: "flex",
          borderBottom: "1px solid #e2e8f0",
          padding: "0 30px",
          background: "#f8fafc"
        }}>
          <button
            onClick={() => setActiveTab("details")}
            className={`tab-button ${activeTab === "details" ? "active" : ""}`}
          >
            <span className="tab-label">Visa Details</span>
            <span className="tab-indicator"></span>
          </button>

          <button
            onClick={() => setActiveTab("enquiry")}
            className={`tab-button ${activeTab === "enquiry" ? "active" : ""}`}
          >
            <span className="tab-label">Enquiry Form</span>
            <span className="tab-indicator"></span>
          </button>
        </div>

        <div style={{ padding: "0 30px" }}>
          {activeTab === "details" ? renderVisaDetails() : renderEnquiryForm()}
        </div>

        <div style={{
          padding: "20px 30px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
        }}>
          <button onClick={closeModal} style={{
            padding: "10px 24px",
            backgroundColor: "#f1f5f9",
            color: "#64748b",
            fontWeight: "600",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            ":hover": { 
              backgroundColor: "#e2e8f0",
              color: "#1e293b"
            }
          }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Styles object for consistent styling
const styles = {
  hero: {
    background: "linear-gradient(to right, #1e3a8a, #3730a3)",
    color: "white",
    padding: "80px 20px",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: "1.875rem",
    fontWeight: "bold",
    color: "#1e293b",
    marginBottom: "16px",
  },
  sectionSubtitle: {
    color: "#64748b",
    maxWidth: "800px",
    margin: "0 auto",
    lineHeight: "1.6",
    marginBottom: "48px"
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "24px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  countryCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.3s, box-shadow 0.3s",
    cursor: "pointer",
    ":hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
    }
  },
  primaryButton: {
    padding: "12px 32px",
    background: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
    color: "white",
    fontWeight: "600",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    position: "relative",
    overflow: "hidden",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
    },
    ":after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
      animation: "shimmer 1.5s infinite"
    }
  },
  secondaryButton: {
    padding: "12px 32px",
    backgroundColor: "transparent",
    color: "white",
    fontWeight: "600",
    borderRadius: "8px",
    border: "2px solid white",
    cursor: "pointer",
    transition: "all 0.3s",
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    }
  },
  selectInput: {
    padding: "12px 24px",
    width: "100%",
    maxWidth: "400px",
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    color: "white",
    outline: "none",
    fontSize: "1rem",
  },
  testimonialCard: {
    backgroundColor: "white",
    padding: "32px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  helpCard: {
    backgroundColor: "white",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    height: "100%",
  },
  flagImage: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    objectFit: "cover",
    marginRight: "16px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    overflow: "hidden"
  }
};

const Visa = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [visaDetails, setVisaDetails] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [destinationCountry, setDestinationCountry] = useState("");

  useEffect(() => {
    const db = getDatabase();
    const visaRef = ref(db, "visa");

    // Fetch visa data from Firebase
    const fetchData = onValue(
      visaRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // First get the country names from Firebase
          const firebaseCountries = Object.keys(data);
          
          // Then fetch additional country data from REST Countries API
          fetch('https://restcountries.com/v3.1/all')
            .then(response => response.json())
            .then(apiCountries => {
              // Map Firebase countries with API data
              const countriesData = firebaseCountries.map((countryName) => {
                // Convert country name to match API format (e.g., "south-africa" to "South Africa")
                const formattedName = countryName.replace(/-/g, ' ');
                
                // Find matching country in API data
                const apiCountry = apiCountries.find(c => 
                  c.name.common.toLowerCase() === formattedName.toLowerCase() ||
                  c.cca2.toLowerCase() === countryCodes[countryName.toLowerCase()]?.toLowerCase()
                );
                
                return {
                  name: countryName,
                  commonName: apiCountry?.name?.common || formattedName,
                  flag: apiCountry?.flags?.svg || apiCountry?.flags?.png || 
                        `https://flagcdn.com/${countryCodes[countryName.toLowerCase()] || "us"}.svg`
                };
              });
              
              setCountries(countriesData);
            })
            .catch(error => {
              console.error("Error fetching country data:", error);
              // Fallback to Firebase data only if API fails
              const countriesData = firebaseCountries.map((country) => ({
                name: country,
                commonName: country.replace(/-/g, " "),
                flag: `https://flagcdn.com/${countryCodes[country.toLowerCase()] || "us"}.svg`,
              }));
              setCountries(countriesData);
            });
        } else {
          setCountries([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching visa data:", error);
        setLoading(false);
      }
    );

    return () => {
      fetchData();
    };
  }, []);

  const fetchVisaDetails = (country) => {
    if (!country) return;
    
    setModalLoading(true);
    setSelectedCountry(country);

    const db = getDatabase();
    const countryRef = ref(db, `visa/${country}`);

    onValue(
      countryRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setVisaDetails(data);
        } else {
          setVisaDetails({});
        }
        setShowModal(true);
        setModalLoading(false);
      },
      (error) => {
        console.error("Error fetching visa details:", error);
        setModalLoading(false);
      }
    );
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCountry(null);
    setVisaDetails({});
  };

  const handleCheckVisaRequirements = () => {
    if (destinationCountry) {
      fetchVisaDetails(destinationCountry);
    }
  };

  const handleCountrySelect = (e) => {
    const country = e.target.value;
    if (country && country !== "Select Destination Country") {
      setDestinationCountry(country);
    } else {
      setDestinationCountry("");
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", backgroundColor: "#f8fafc" }}>

        <SEO
        title="Visa Assistance | Pearl Tours & Travels"
        description="Get hassle-free visa assistance for your international travel with Pearl Tours & Travels."
        keywords="visa services, travel visa, tourist visa, international trips, Pearl Tours visa assistance"
      />
      <Navbar />
      
      <VisaDetailModal 
        showModal={showModal}
        closeModal={closeModal}
        selectedCountry={selectedCountry}
        visaDetails={visaDetails}
        modalLoading={modalLoading}
      />

      {/* Hero Section */}
      <div style={{
        background: "linear-gradient(135deg, #6e8efb, #a777e3)",
        color: "white",
        padding: "100px 0",
        textAlign: "center",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{ 
          maxWidth: "1200px", 
          margin: "0 auto", 
          padding: "0 20px",
          position: "relative",
          zIndex: 2
        }}>
          <h1 style={{ 
            fontSize: "3rem",
            fontWeight: "800",
            marginBottom: "24px",
            lineHeight: "1.2",
            textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            transform: "translateY(20px)",
            opacity: 0,
            animation: "fadeInUp 0.8s forwards 0.2s"
          }}>
            Global Visa Solutions Made Simple
          </h1>
          
          <p style={{ 
            fontSize: "1.35rem",
            marginBottom: "40px",
            maxWidth: "800px",
            margin: "0 auto 32px",
            lineHeight: "1.6",
            opacity: 0,
            transform: "translateY(20px)",
            animation: "fadeInUp 0.8s forwards 0.4s"
          }}>
            Expert visa services with robust methods and streamlined processes for hassle-free travel
          </p>
          
          <div style={{ 
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            marginBottom: "48px",
            opacity: 0,
            transform: "translateY(20px)",
            animation: "fadeInUp 0.8s forwards 0.6s"
          }}>
            <select style={{
              width: "100%",
              maxWidth: "400px",
              padding: "15px 20px",
              borderRadius: "50px",
              border: "none",
              fontSize: "1rem",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              appearance: "none",
              background: "white url('data:image/svg+xml;charset=US-ASCII,<svg width=\"16\" height=\"16\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M6 9L12 15L18 9\" stroke=\"%23666666\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg>') no-repeat right 20px center",
              transition: "all 0.3s ease",
              cursor: "pointer",
              ":hover": {
                boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                transform: "translateY(-2px)"
              },
              ":focus": {
                outline: "none",
                boxShadow: "0 0 0 3px rgba(110,142,251,0.3)"
              }
            }} defaultValue="India">
              <option value="India" style={{ color: "black" }}>India</option>
            </select>
            
            <select style={{
              width: "100%",
              maxWidth: "400px",
              padding: "15px 20px",
              borderRadius: "50px",
              border: "none",
              fontSize: "1rem",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              appearance: "none",
              background: "white url('data:image/svg+xml;charset=US-ASCII,<svg width=\"16\" height=\"16\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M6 9L12 15L18 9\" stroke=\"%23666666\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/></svg>') no-repeat right 20px center",
              transition: "all 0.3s ease",
              cursor: "pointer",
              ":hover": {
                boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                transform: "translateY(-2px)"
              },
              ":focus": {
                outline: "none",
                boxShadow: "0 0 0 3px rgba(110,142,251,0.3)"
              }
            }} 
              onChange={handleCountrySelect}
              value={destinationCountry}
            >
              <option value="Select Destination Country" style={{ color: "#666" }}>
                Select Destination Country
              </option>
              {countries.map((country, index) => (
                <option key={index} value={country.name} style={{ color: "black" }}>
                  {country.commonName}
                </option>
              ))}
            </select>
            
            <button style={{
              width: "100%",
              maxWidth: "400px",
              padding: "16px 32px",
              borderRadius: "50px",
              border: "none",
              fontSize: "1.1rem",
              fontWeight: "600",
              color: "white",
              background: "linear-gradient(to right, #4facfe 0%, #00f2fe 100%)",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              cursor: destinationCountry ? "pointer" : "not-allowed",
              transition: "all 0.4s ease",
              transform: destinationCountry ? "scale(1)" : "scale(0.98)",
              opacity: destinationCountry ? 1 : 0.7,
              ":hover": {
                transform: destinationCountry ? "translateY(-3px) scale(1.02)" : "scale(0.98)",
                boxShadow: destinationCountry ? "0 8px 25px rgba(0,0,0,0.3)" : "0 4px 15px rgba(0,0,0,0.2)",
                background: destinationCountry ? "linear-gradient(to right, #3ea1fd 0%, #00e1f9 100%)" : "linear-gradient(to right, #4facfe 0%, #00f2fe 100%)"
              },
              ":active": {
                transform: "scale(0.98)"
              },
              animation: destinationCountry ? "pulse 2s infinite" : "none"
            }}
              onClick={handleCheckVisaRequirements}
              disabled={!destinationCountry}
            >
              Check Visa Requirements
            </button>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div style={{
          position: "absolute",
          top: "-100px",
          right: "-100px",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          zIndex: 1
        }}></div>
        
        <div style={{
          position: "absolute",
          bottom: "-150px",
          left: "-100px",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          zIndex: 1
        }}></div>
      </div>

      {/* Popular Visas Section */}
<div style={{ 
  backgroundColor: "#f8fafc", 
  padding: "80px 20px",
  position: "relative",
  overflow: "hidden"
}}>
  {/* Decorative elements */}
  <div style={{
    position: "absolute",
    top: 0,
    right: 0,
    width: "300px",
    height: "300px",
    background: "radial-gradient(circle, rgba(37, 99, 235, 0.1) 0%, rgba(255,255,255,0) 70%)",
    transform: "translate(30%, -30%)",
    zIndex: 0
  }}></div>
  
  <div style={{ 
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "200px",
    height: "200px",
    background: "radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, rgba(255,255,255,0) 70%)",
    transform: "translate(-30%, 30%)",
    zIndex: 0
  }}></div>

  <div style={{ 
    maxWidth: "1280px", 
    margin: "0 auto", 
    textAlign: "center",
    position: "relative",
    zIndex: 1
  }}>
    <div style={{ marginBottom: "48px" }}>
      <h2 style={{
        fontSize: "2.5rem",
        fontWeight: "700",
        color: "#1e293b",
        marginBottom: "16px",
        background: "linear-gradient(90deg, #1e3a8a, #3b82f6)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        display: "inline-block"
      }}>
        Visa for Popular Countries
      </h2>
      <p style={{
        fontSize: "1.125rem",
        color: "#64748b",
        maxWidth: "700px",
        margin: "0 auto",
        lineHeight: "1.6"
      }}>
        Discover seamless visa applications for our most sought-after destinations with expert guidance at every step
      </p>
    </div>

    {loading ? (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "300px",
        flexDirection: "column"
      }}>
        <div style={{
          border: "4px solid rgba(37, 99, 235, 0.1)",
          borderTopColor: "#2563eb",
          borderRadius: "50%",
          width: "50px",
          height: "50px",
          animation: "spin 1s linear infinite",
          marginBottom: "16px"
        }}></div>
        <p style={{ color: "#64748b", fontSize: "1rem" }}>Loading visa information...</p>
      </div>
    ) : countries.length > 0 ? (
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: "24px",
        padding: "0 20px"
      }}>
        {countries.map((country, index) => (
          <div
            key={index}
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              overflow: "hidden",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e2e8f0",
              position: "relative",
              transform: hoveredCard === index ? "translateY(-8px)" : "none",
              boxShadow: hoveredCard === index ? "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" : "0 4px 6px rgba(0, 0, 0, 0.05)",
              zIndex: hoveredCard === index ? 10 : 1
            }}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => fetchVisaDetails(country.name)}
          >
            <div style={{ 
              padding: "24px", 
              display: "flex", 
              flexDirection: "column",
              height: "100%"
            }}>
              <div style={{ 
                display: "flex", 
                alignItems: "center",
                marginBottom: "20px"
              }}>
                <div style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "12px",
                  backgroundColor: "#eff6ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                }}>
                  {country.flag ? (
                    <img
                      src={country.flag}
                      alt={country.commonName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.textContent = country.commonName
                          .split(" ")
                          .map(word => word[0])
                          .join("")
                          .substring(0, 2)
                          .toUpperCase();
                      }}
                    />
                  ) : (
                    <span style={{
                      fontSize: "1.25rem",
                      fontWeight: "600",
                      color: "#2563eb"
                    }}>
                      {country.commonName
                        .split(" ")
                        .map(word => word[0])
                        .join("")
                        .substring(0, 2)
                        .toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={{ 
                  flex: 1, 
                  textAlign: "left",
                  marginLeft: "16px"
                }}>
                  <h3 style={{ 
                    fontSize: "1.25rem", 
                    fontWeight: "600", 
                    color: "#1e293b", 
                    marginBottom: "4px"
                  }}>
                    {country.commonName}
                  </h3>
                  <p style={{
                    fontSize: "0.875rem",
                    color: "#64748b",
                    margin: 0
                  }}>
                    {country.visaType || "Tourist Visa"}
                  </p>
                </div>
              </div>
              
              <div style={{ 
                marginTop: "auto",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchVisaDetails(country.name);
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                    color: "white",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    boxShadow: "0 2px 4px rgba(37, 99, 235, 0.2)",
                    ":hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 8px rgba(37, 99, 235, 0.3)",
                    }
                  }}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div style={{ 
        textAlign: "center", 
        padding: "60px 20px", 
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)"
      }}>
        <div style={{
          width: "80px",
          height: "80px",
          backgroundColor: "#eff6ff",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px"
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 8V12" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 16H12.01" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 style={{
          fontSize: "1.25rem",
          fontWeight: "600",
          color: "#1e293b",
          marginBottom: "8px"
        }}>
          No Visa Information Available
        </h3>
        <p style={{
          fontSize: "1rem",
          color: "#64748b",
          maxWidth: "500px",
          margin: "0 auto",
          lineHeight: "1.6"
        }}>
          We're currently updating our visa information. Please check back later or contact our support team.
        </p>
      </div>
    )}
  </div>
</div>

      {/* Why Choose Us Section */}
      <div style={{ 
        background: "linear-gradient(to bottom, #f8fafc 0%, #f0f9ff 100%)",
        padding: "100px 20px",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto",
          position: "relative",
          zIndex: 2
        }}>
          <div style={{ 
            textAlign: "center", 
            marginBottom: "70px",
            padding: "0 20px"
          }}>
            <h2 style={{
              fontSize: "2.75rem",
              fontWeight: "800",
              color: "#1e293b",
              marginBottom: "18px",
              lineHeight: "1.2",
              background: "linear-gradient(90deg, #1e3a8a 0%, #1e40af 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block"
            }}>
              Why Choose Our Visa Services?
            </h2>
            <p style={{
              maxWidth: "700px",
              margin: "0 auto",
              fontSize: "1.125rem",
              color: "#475569",
              lineHeight: "1.7"
            }}>
              Making the complex visa process a smooth task for you, we aspire to be your first choice for all visa needs
            </p>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "30px",
            padding: "0 20px"
          }}>
            {strengths.map((item, index) => (
              <div key={index} style={{
                position: "relative",
                background: "white",
                borderRadius: "20px",
                padding: "40px 30px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.03)",
                transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.1)",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                zIndex: 1,
                ":hover": {
                  transform: "translateY(-10px) scale(1.02)",
                  boxShadow: "0 20px 25px -5px rgba(37, 99, 235, 0.15), 0 10px 10px -5px rgba(37, 99, 235, 0.05)",
                  borderColor: "#93c5fd"
                },
                ":before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)",
                  transition: "all 0.3s ease"
                },
                ":hover:before": {
                  height: "6px",
                  background: "linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)"
                }
              }}>
                <div style={{ 
                  display: "flex",
                  alignItems: "flex-start",
                  height: "100%",
                  position: "relative",
                  zIndex: 2
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                    width: "56px",
                    height: "56px",
                    borderRadius: "14px",
                    marginRight: "20px",
                    flexShrink: 0,
                    boxShadow: "0 4px 6px rgba(59, 130, 246, 0.25)",
                    transition: "all 0.3s ease"
                  }}>
                    <span style={{ 
                      fontSize: "1.75rem", 
                      color: "white",
                      transition: "all 0.3s ease"
                    }}>
                      {item.icon}
                    </span>
                  </div>
                  <div>
                    <h3 style={{ 
                      fontSize: "1.3rem", 
                      fontWeight: "700", 
                      color: "#1e293b", 
                      marginBottom: "14px",
                      transition: "all 0.3s ease"
                    }}>
                      {item.title}
                    </h3>
                    <p style={{ 
                      color: "#64748b", 
                      lineHeight: "1.7",
                      fontSize: "1.05rem",
                      transition: "all 0.3s ease"
                    }}>
                      {item.description}
                    </p>
                  </div>
                </div>
                <div style={{
                  position: "absolute",
                  bottom: "-50px",
                  right: "-50px",
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%)",
                  transition: "all 0.5s ease",
                  zIndex: 1
                }}></div>
              </div>
            ))}
          </div>
        </div>
      </div>

    {/* How It Works Section */}
<div className="how-it-works-container" style={{ backgroundColor: "white", padding: "64px 20px" }}>
  <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
    <h2 style={styles.sectionTitle}>
      How to Apply for Your Visa
    </h2>
    <p style={styles.sectionSubtitle}>
      Obtaining a visa can be a daunting task, but we've simplified it into just a few easy steps
    </p>

    <div className="steps-container" style={{
      display: "grid",
      gridAutoFlow: "column",
      gridAutoColumns: "minmax(280px, 1fr)",
      gap: "32px",
      maxWidth: "1200px",
      margin: "0 auto",
      overflow: "hidden",
      padding: "20px 0"
    }}>
      {steps.map((step, index) => (
        <div 
          className="step-card"
          key={index}
          style={{
            position: "relative",
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
            overflow: "hidden",
            border: "1px solid #f1f5f9",
            transform: "translateY(0)",
            ":hover": {
              transform: "translateY(-8px) scale(1.02)",
              boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)",
              "::after": {
                transform: "scaleX(1)"
              },
              "> div:first-child": {
                backgroundColor: "#dbeafe",
                transform: "rotate(5deg) scale(1.05)"
              },
              "> h3": {
                color: "#2563eb",
                "::after": {
                  transform: "scaleX(1)",
                  opacity: 1
                }
              },
              "> p": {
                transform: "translateY(-2px)",
                opacity: 1
              },
              "> div:last-child": {
                transform: "scale(1.2)",
                backgroundColor: "#2563eb",
                color: "white"
              }
            },
            "::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "3px",
              backgroundColor: "#2563eb",
              transform: "scaleX(0)",
              transformOrigin: "left center",
              transition: "transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)"
            }
          }}
        >
          <div style={{
            width: "60px",
            height: "60px",
            borderRadius: "12px",
            backgroundColor: "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "24px",
            transition: "all 0.4s ease",
          }}>
            <span style={{ 
              fontSize: "1.75rem",
              color: "#1e293b",
              transition: "all 0.4s ease"
            }}>
              {step.icon}
            </span>
          </div>
          
          <h3 style={{
            fontSize: "1.25rem",
            fontWeight: "600",
            color: "#1e293b",
            marginBottom: "16px",
            position: "relative",
            display: "inline-block",
            transition: "color 0.3s ease",
            "::after": {
              content: '""',
              position: "absolute",
              bottom: "-4px",
              left: 0,
              width: "40px",
              height: "2px",
              backgroundColor: "#2563eb",
              transform: "scaleX(0)",
              transformOrigin: "left center",
              transition: "transform 0.4s ease, opacity 0.3s ease",
              opacity: 0.7
            }
          }}>
            {step.title}
          </h3>
          
          <p style={{
            color: "#64748b",
            lineHeight: "1.6",
            opacity: 0.9,
            transform: "translateY(0)",
            transition: "all 0.4s ease",
            marginBottom: "16px"
          }}>
            {step.description || "Our streamlined process ensures quick approval with minimal documentation required."}
          </p>
          
          <div style={{
            position: "absolute",
            top: "32px",
            right: "32px",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.75rem",
            fontWeight: "600",
            color: "#64748b",
            transition: "all 0.4s ease"
          }}>
            {index + 1}
          </div>
        </div>
      ))}
    </div>
  </div>
</div>

      {/* Testimonials Section */}
      <div style={{ 
        background: "linear-gradient(to right, #e0f2fe, #e0e7ff)", 
        padding: "64px 20px" 
      }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
          <p style={{ color: "#2563eb", fontWeight: "500", marginBottom: "8px" }}>
            Testimonials
          </p>
          <h2 style={styles.sectionTitle}>
            Delighted Travelers
          </h2>
          <div style={{ 
            display: "flex", 
            justifyContent: "center", 
            color: "#f59e0b", 
            fontSize: "1.5rem", 
            marginBottom: "16px" 
          }}>
            {[...Array(5)].map((_, i) => <span key={i} style={{ margin: "0 2px" }}>★</span>)}
          </div>
          <p style={styles.sectionSubtitle}>
            Hear from travelers who have experienced our visa services firsthand
          </p>
        </div>

        <div style={styles.cardGrid}>
          {testimonials.map((testimonial, index) => (
            <Card key={index} style={{ transition: "all 0.3s", ":hover": { transform: "translateY(-5px)" } }}>
              <div style={{ display: "flex", color: "#f59e0b", marginBottom: "16px" }}>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i} style={{ marginRight: "4px" }}>★</span>
                ))}
              </div>
              <p style={{ 
                color: "#334155", 
                fontSize: "1.125rem", 
                fontStyle: "italic", 
                marginBottom: "24px", 
                lineHeight: "1.6" 
              }}>
                "{testimonial.quote}"
              </p>
              <p style={{ fontWeight: "600", color: "#1e293b" }}>
                {testimonial.author}
              </p>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      {/* <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px"
       }}>
        <div style={{
          width: "1440px",
          height: "633px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          overflow: "hidden"
        }}>
          <div style={{
            position: "absolute",
            top: "-150px",
            right: "-150px",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            zIndex: 1
          }}></div> */}
          
          {/* <div style={{
            position: "absolute",
            bottom: "-200px",
            left: "-150px",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            zIndex: 1
          }}></div> */}

          {/* <h2 style={{ 
            fontSize: "3.5rem",
            fontWeight: "800",
            marginBottom: "24px",
            background: "linear-gradient(90deg, #ffffff, #e0e9ff)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            letterSpacing: "-0.5px",
            lineHeight: "1.2",
            textShadow: "0 2px 10px rgba(0,0,0,0.3)",
            opacity: 0,
            transform: "translateY(20px)",
            animation: "fadeInUp 0.8s forwards 0.2s",
            position: "relative",
            zIndex: 2
          }}>
            Begin Your Visa Journey
          </h2> */}
          
          {/* <p style={{ 
            fontSize: "1.35rem",
            marginBottom: "48px",
            maxWidth: "700px",
            lineHeight: "1.6",
            color: "rgba(255, 255, 255, 0.9)",
            textShadow: "0 1px 3px rgba(0,0,0,0.3)",
            opacity: 0,
            transform: "translateY(20px)",
            animation: "fadeInUp 0.8s forwards 0.4s",
            position: "relative",
            zIndex: 2,
            textAlign: "center"
          }}>
            Let our specialists guide you through every step of the process
          </p>
          
          <div style={{ 
            display: "flex",
            justifyContent: "center",
            gap: "24px",
            flexWrap: "wrap",
            opacity: 0,
            transform: "translateY(20px)",
            animation: "fadeInUp 0.8s forwards 0.6s",
            position: "relative",
            zIndex: 2
          }}>
            <button style={{ 
              padding: "18px 42px",
              fontSize: "1.15rem",
              fontWeight: "600",
              borderRadius: "50px",
              border: "none",
              cursor: "pointer",
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
              boxShadow: `
                0 4px 15px rgba(0, 0, 0, 0.2),
                0 0 20px rgba(79, 172, 254, 0.4),
                inset 0 0 10px rgba(255, 255, 255, 0.3)
              `,
              position: "relative",
              overflow: "hidden",
              zIndex: 1,
              ":hover": {
                transform: "translateY(-5px) scale(1.02)",
                boxShadow: `
                  0 8px 25px rgba(0, 0, 0, 0.3),
                  0 0 30px rgba(79, 172, 254, 0.6),
                  inset 0 0 15px rgba(255, 255, 255, 0.4)
                `,
                background: "linear-gradient(135deg, #3ea1fd 0%, #00e1f9 100%)"
              },
              "::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "linear-gradient(90deg, rgba(255,255,255,0.3), transparent)",
                transform: "translateX(-100%)",
                transition: "transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
                zIndex: -1
              },
              ":hover::after": {
                transform: "translateX(100%)"
              }
            }}>
              <span style={{ 
                position: "relative",
                zIndex: 2,
                textShadow: "0 1px 3px rgba(0,0,0,0.2)"
              }}>
                Visa Consultation
              </span>
            </button>
            
            <button style={{
              padding: "18px 42px",
              fontSize: "1.15rem",
              fontWeight: "600",
              borderRadius: "50px",
              cursor: "pointer",
              transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              backgroundColor: "transparent",
              color: "white",
              border: "2px solid rgba(255,255,255,0.4)",
              boxShadow: `
                0 4px 15px rgba(0, 0, 0, 0.1),
                0 0 0 1px rgba(255,255,255,0.2),
                0 0 20px rgba(255,255,255,0.1)
              `,
              position: "relative",
              overflow: "hidden",
              zIndex: 1,
              ":hover": {
                transform: "translateY(-5px) scale(1.02)",
                boxShadow: `
                  0 8px 25px rgba(0, 0, 0, 0.2),
                  0 0 0 1px rgba(255,255,255,0.4),
                  0 0 30px rgba(255,255,255,0.2)
                `,
                backgroundColor: "rgba(255,255,255,0.1)",
                borderColor: "rgba(255,255,255,0.6)",
                "::before": {
                  opacity: 1,
                  transform: "scale(1)"
                }
              },
              "::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "linear-gradient(135deg, rgba(255,255,255,0.2), transparent)",
                opacity: 0,
                transform: "scale(0.8)",
                transition: "all 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
                zIndex: -1
              }
            }}>
              <span style={{ 
                position: "relative",
                zIndex: 2,
                textShadow: "0 1px 3px rgba(0,0,0,0.2)"
              }}>
                Expert Assistance
              </span>
            </button>
          </div>
        </div>
      </div> */}

      {/* Help Section */}
      <div style={{
        background: "radial-gradient(circle at 10% 20%, #f0f9ff 0%, #e6f3ff 100%)",
        padding: "80px 20px",
        overflow: "hidden"
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "40px",
          maxWidth: "1300px",
          margin: "0 auto",
          perspective: "2000px"
        }}>
          <div style={{
            background: "linear-gradient(145deg, #ffffff, #f8faff)",
            borderRadius: "24px",
            padding: "40px",
            boxShadow: "0 15px 40px -10px rgba(37, 99, 235, 0.25)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
            position: "relative",
            overflow: "hidden",
            transformStyle: "preserve-3d",
            ":hover": {
              transform: "translateY(-15px) rotateX(5deg) scale(1.03)",
              boxShadow: "0 25px 50px -15px rgba(37, 99, 235, 0.4)",
              borderColor: "rgba(59, 130, 246, 0.4)"
            }
          }}>
            <div style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "200px",
              height: "200px",
              background: "radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)",
              zIndex: 1
            }}></div>
            
            <div style={{ position: "relative", zIndex: 2 }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px"
              }}>
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "22px",
                  background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "20px",
                  boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.5)",
                  transition: "transform 0.4s ease",
                  ":hover": {
                    transform: "rotate(15deg) scale(1.1)"
                  }
                }}>
                  <span style={{ 
                    fontSize: "2.5rem", 
                    color: "white",
                    filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.1))"
                  }}>📞</span>
                </div>
                <h3 style={{ 
                  fontSize: "1.8rem", 
                  fontWeight: "800", 
                  background: "linear-gradient(90deg, #2563eb, #1e40af)",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  margin: 0
                }}>Need Help?</h3>
              </div>
              
              <ul style={{ 
                listStyle: "none", 
                padding: 0, 
                margin: 0, 
                display: "grid", 
                gap: "18px"
               }}>
                {[
                  { icon: "📞", title: "Call Us", text: "+9173034 55505", sub: "24/7 Support" },
                  { icon: "✉️", title: "Email", text: "pearltoursandtravelsangli@gmail.com", sub: "Fast Response" },
                  { icon: "💬", title: "WhatsApp", text: "+9173034 55505", sub: "Instant Chat" }
                ].map((item, index) => (
                  <li key={index} style={{ 
                    display: "flex", 
                    alignItems: "center",
                    padding: "16px",
                    borderRadius: "14px",
                    background: "rgba(239, 246, 255, 0.6)",
                    transition: "all 0.4s ease",
                    ":hover": {
                      transform: "translateX(10px)",
                      background: "rgba(219, 234, 254, 0.8)",
                      boxShadow: "0 8px 20px -5px rgba(37, 99, 235, 0.2)"
                    }
                  }}>
                    <div style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "14px",
                      background: "rgba(59, 130, 246, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "16px",
                      fontSize: "1.5rem"
                    }}>{item.icon}</div>
                    <div>
                      <div style={{ fontWeight: "700", color: "#1e40af" }}>{item.title}</div>
                      <div style={{ fontWeight: "600", color: "#1e293b", fontSize: "1.1rem" }}>{item.text}</div>
                      <div style={{ color: "#64748b", fontSize: "0.9rem" }}>{item.sub}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{
            background: "linear-gradient(145deg, #ffffff, #f0fdf4)",
            borderRadius: "24px",
            padding: "40px",
            boxShadow: "0 15px 40px -10px rgba(16, 185, 129, 0.25)",
            border: "1px solid rgba(34, 197, 94, 0.2)",
            transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
            position: "relative",
            overflow: "hidden",
            transformStyle: "preserve-3d",
            ":hover": {
              transform: "translateY(-15px) rotateX(5deg) scale(1.03)",
              boxShadow: "0 25px 50px -15px rgba(16, 185, 129, 0.4)",
              borderColor: "rgba(34, 197, 94, 0.4)"
            }
          }}>
            <div style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "200px",
              height: "200px",
              background: "radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)",
              zIndex: 1
            }}></div>
            
            <div style={{ position: "relative", zIndex: 2 }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px"
              }}>
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "22px",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "20px",
                  boxShadow: "0 10px 25px -5px rgba(16, 185, 129, 0.5)",
                  transition: "transform 0.4s ease",
                  ":hover": {
                    transform: "rotate(-15deg) scale(1.1)"
                  }
                }}>
                  <span style={{ 
                    fontSize: "2.5rem", 
                    color: "white",
                    filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.1))"
                  }}>⭐</span>
                </div>
                <h3 style={{ 
                  fontSize: "1.8rem", 
                  fontWeight: "800", 
                  background: "linear-gradient(90deg, #10b981, #047857)",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  margin: 0
                }}>Book With Confidence</h3>
              </div>
              
              <ul style={{ 
                listStyle: "none", 
                padding: 0, 
                margin: 0, 
                display: "grid", 
                gap: "18px"
              }}>
                {[
                  { icon: "💯", text: "Best Price Guarantee" },
                  { icon: "🌐", text: "24/7 Customer Support" },
                  { icon: "✋", text: "Hand-Picked Experiences" },
                  { icon: "🛡️", text: "Free Travel Insurance" }
                ].map((item, index) => (
                  <li key={index} style={{ 
                    display: "flex", 
                    alignItems: "center",
                    padding: "16px",
                    borderRadius: "14px",
                    background: "rgba(220, 252, 231, 0.6)",
                    transition: "all 0.4s ease",
                    ":hover": {
                      transform: "translateX(10px)",
                      background: "rgba(187, 247, 208, 0.8)",
                      boxShadow: "0 8px 20px -5px rgba(16, 185, 129, 0.2)"
                    }
                  }}>
                    <div style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "14px",
                      background: "rgba(16, 185, 129, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "16px",
                      fontSize: "1.5rem"
                    }}>{item.icon}</div>
                    <div style={{ fontWeight: "600", color: "#064e3b" }}>{item.text}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{
            background: "linear-gradient(145deg, #ffffff, #f0f9ff)",
            borderRadius: "24px",
            padding: "40px",
            boxShadow: "0 15px 40px -10px rgba(14, 165, 233, 0.25)",
            border: "1px solid rgba(56, 189, 248, 0.2)",
            transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
            position: "relative",
            overflow: "hidden",
            transformStyle: "preserve-3d",
            ":hover": {
              transform: "translateY(-15px) rotateX(5deg) scale(1.03)",
              boxShadow: "0 25px 50px -15px rgba(14, 165, 233, 0.4)",
              borderColor: "rgba(56, 189, 248, 0.4)"
            }
          }}>
            <div style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "200px",
              height: "200px",
              background: "radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 70%)",
              zIndex: 1
            }}></div>
            
            <div style={{ position: "relative", zIndex: 2 }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "30px"
              }}>
                <div style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "22px",
                  background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "20px",
                  boxShadow: "0 10px 25px -5px rgba(14, 165, 233, 0.5)",
                  transition: "transform 0.4s ease",
                  ":hover": {
                    transform: "rotate(15deg) scale(1.1)"
                  }
                }}>
                  <span style={{ 
                    fontSize: "2.5rem", 
                    color: "white",
                    filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.1))"
                  }}>✈️</span>
                </div>
                <h3 style={{ 
                  fontSize: "1.8rem", 
                  fontWeight: "800", 
                  background: "linear-gradient(90deg, #0ea5e9, #0369a1)",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  margin: 0
                }}>Visa Tips</h3>
              </div>
              
              <ul style={{ 
                listStyle: "none", 
                padding: 0, 
                margin: 0, 
                display: "grid", 
                gap: "18px"
              }}>
                {[
                  { icon: "⏱️", text: "Apply 4-6 weeks before travel" },
                  { icon: "📄", text: "Clear, legible documents" },
                  { icon: "🛂", text: "6+ months passport validity" },
                  { icon: "💾", text: "Keep digital copies" }
                ].map((item, index) => (
                  <li key={index} style={{ 
                    display: "flex", 
                    alignItems: "center",
                    padding: "16px",
                    borderRadius: "14px",
                    background: "rgba(224, 242, 254, 0.6)",
                    transition: "all 0.4s ease",
                    ":hover": {
                      transform: "translateX(10px)",
                      background: "rgba(186, 230, 253, 0.8)",
                      boxShadow: "0 8px 20px -5px rgba(14, 165, 233, 0.2)"
                    }
                  }}>
                    <div style={{
                      width: "50px",
                      height: "50px",
                      borderRadius: "14px",
                      background: "rgba(14, 165, 233, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "16px",
                      fontSize: "1.5rem"
                    }}>{item.icon}</div>
                    <div style={{ fontWeight: "600", color: "#075985" }}>{item.text}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      
      {/* Global Animations */}
      <style jsx global>{`
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7); }
    70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .tab-button {
    position: relative;
    padding: 16px 24px;
    background: none;
    border: none;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    color: #64748b;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .tab-button:hover {
    color: #2563eb;
    background-color: rgba(37, 99, 235, 0.05);
  }
  
  .tab-button.active {
    color: #2563eb;
    font-weight: 700;
  }
  
  .tab-label {
    position: relative;
    z-index: 1;
    transition: transform 0.3s ease;
  }
  
  .tab-button:hover .tab-label {
    transform: translateY(-2px);
  }
  
  .tab-indicator {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: #2563eb;
    border-radius: 3px 3px 0 0;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .tab-button.active .tab-indicator {
    transform: scaleX(1);
  }
  
  .tab-button:hover .tab-indicator {
    transform: scaleX(0.5);
    background: rgba(37, 99, 235, 0.3);
  }
  
  .tab-button.active:hover .tab-indicator {
    transform: scaleX(1);
    background: #2563eb;
  }
  
  .tab-button::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to bottom right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) 45%,
      rgba(255, 255, 255, 0.15) 50%,
      rgba(255, 255, 255, 0) 55%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(30deg);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  .tab-button:hover::before {
    opacity: 1;
    animation: shine 1.5s infinite;
  }
  
  @keyframes shine {
    0% {
      transform: translateX(-100%) rotate(30deg);
    }
    100% {
      transform: translateX(100%) rotate(30deg);
    }
  }

  /* Horizontal scroller styles for mobile */
  @media (max-width: 768px) {
    .how-it-works-container {
      padding: 48px 16px !important;
    }
    
    .steps-container {
      display: flex !important;
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
      overflow-y: hidden !important;
      scroll-snap-type: x mandatory !important;
      -webkit-overflow-scrolling: touch !important;
      padding: 20px 16px !important;
      margin: 0 -16px !important;
      gap: 24px !important;
      scroll-padding: 16px !important;
    }
    
    .step-card {
      min-width: 280px !important;
      scroll-snap-align: start !important;
      flex: 0 0 auto !important;
      transform: none !important;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08) !important;
    }
    
    .step-card:hover {
      transform: none !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
    
    /* Hide scrollbar but keep functionality */
    .steps-container::-webkit-scrollbar {
      display: none !important;
    }
    
    .steps-container {
      -ms-overflow-style: none !important;
      scrollbar-width: none !important;
    }
    
    /* Add some space at the end for better scrolling */
    .steps-container::after {
      content: '';
      padding-right: 16px;
    }
  }

  /* For very small screens (390px and below) */
  @media (max-width: 400px) {
    .step-card {
      min-width: calc(100vw - 64px) !important;
      width: calc(100vw - 64px) !important;
    }
    
    .steps-container {
      gap: 16px !important;
    }
  }
`}</style>
    </div>
  );
};

export default Visa;